"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const keyContacts = [{
  name: "AMIN AREBI",
  role: "Chairman",
  email: "chairman@birkalla.com.au"
}, {
  name: "MARCUS VINCENZO",
  role: "Junior Technical Director",
  email: "jtd@birkalla.com.au"
}, {
  name: "SARAH JENKINS",
  role: "Head of Women's Football",
  email: "women@birkalla.com.au"
}, {
  name: "JOHN SMITH",
  role: "Senior Football Manager",
  email: "senior@birkalla.com.au"
}, {
  name: "DAVID ROSSI",
  role: "Technical Director (Seniors)",
  email: "td@birkalla.com.au"
}, {
  name: "ELENA KOSTAS",
  role: "Operations Manager",
  email: "ops@birkalla.com.au"
}, {
  name: "MICHAEL CHEN",
  role: "Youth Coordinator",
  email: "youth@birkalla.com.au"
}, {
  name: "SOPHIE MARIN",
  role: "MiniRoos Coordinator",
  email: "miniroos@birkalla.com.au"
}, {
  name: "ROBERT BLAKE",
  role: "Equipment Officer",
  email: "kit@birkalla.com.au"
}, {
  name: "LISA WONG",
  role: "Registrar",
  email: "registrar@birkalla.com.au"
}, {
  name: "CHRIS EVANS",
  role: "Media Officer",
  email: "media@birkalla.com.au"
}, {
  name: "ANNA PAVLOV",
  role: "Child Safety Officer",
  email: "safety@birkalla.com.au"
}, {
  name: "TOM HARRIS",
  role: "Sponsorship Manager",
  email: "partners@birkalla.com.au"
}, {
  name: "KEVIN HART",
  role: "Facilities Manager",
  email: "grounds@birkalla.com.au"
}];
const coachingStaff = [{
  category: "Senior Men First Team",
  name: "David Rossi",
  role: "Head Coach",
  email: "d.rossi@birkalla.com.au"
}, {
  category: "Senior Men First Team",
  name: "Paul Vella",
  role: "Assistant Coach",
  email: "p.vella@birkalla.com.au"
}, {
  category: "Senior Women",
  name: "Sarah Jenkins",
  role: "Head Coach",
  email: "s.jenkins@birkalla.com.au"
}, {
  category: "Senior Women",
  name: "Lucy Gray",
  role: "Assistant Coach",
  email: "l.gray@birkalla.com.au"
}, {
  category: "Reserves",
  name: "Mark Thompson",
  role: "Head Coach",
  email: "m.thompson@birkalla.com.au"
}, {
  category: "U18 Academy",
  name: "James Miller",
  role: "Lead Coach",
  email: "j.miller@birkalla.com.au"
}, {
  category: "U16 Academy",
  name: "Chris Poulos",
  role: "Lead Coach",
  email: "c.poulos@birkalla.com.au"
}, {
  category: "U14 Academy",
  name: "Nick Santos",
  role: "Lead Coach",
  email: "n.santos@birkalla.com.au"
}, {
  category: "U12 Academy",
  name: "Adrian Cole",
  role: "Lead Coach",
  email: "a.cole@birkalla.com.au"
}, {
  category: "Junior Development",
  name: "Sam G",
  role: "Skills Coach",
  email: "skills@birkalla.com.au"
}];
const categories = Array.from(new Set(coachingStaff.map(s => s.category)));
export default function TheArchitectsOfSpeech() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contactsRef,
    isVisible: isContactsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: coachingRef,
    isVisible: isCoachingVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="234477" id="the-coaching-staff" className="bg-background text-foreground py-24 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-6xl font-extrabold tracking-tighter uppercase mb-4 text-foreground">
            THE COACHING STAFF
          </h2>
          <div className="h-1 w-24 bg-primary mx-auto mb-6" />
          <p className="text-muted-foreground text-lg font-sans">
            Led by elite technical directors, our staff is committed to the development of every player at WT Birkalla.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {}
          <div ref={contactsRef} className={`lg:col-span-5 transition-all duration-700 delay-100 ${isContactsVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <h3 className="font-serif text-2xl font-bold tracking-tight uppercase mb-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-primary" />
              Key Contacts
            </h3>
            <div className="space-y-4">
              {keyContacts.map((contact, i) => <div key={contact.name} data-index={i} className="group flex flex-col border-b border-border/50 pb-4 hover:border-primary/50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-primary font-bold mb-1">
                        {contact.role}
                      </p>
                      <h4 className="font-serif text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                        {contact.name}
                      </h4>
                    </div>
                    <Link to={`mailto:${contact.email}`} className="p-2 rounded-none bg-muted hover:bg-primary hover:text-primary-foreground transition-all" aria-label={`Email ${contact.name}`}>
                      <Mail className="w-4 h-4" />
                    </Link>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div ref={coachingRef} className={`lg:col-span-7 transition-all duration-700 delay-200 ${isCoachingVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <h3 className="font-serif text-2xl font-bold tracking-tight uppercase mb-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-primary" />
              Coaching Staff
            </h3>
            
            <Accordion type="single" collapsible className="w-full space-y-2">
              {categories.map((category, i) => <AccordionItem key={category} value={`item-${i}`} data-index={i} className="border border-border bg-card/50 px-4 rounded-none">
                  <AccordionTrigger className="hover:no-underline py-6 group">
                    <span className="font-serif text-lg font-bold uppercase tracking-tight group-hover:text-primary transition-colors">
                      {category}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                      {coachingStaff.filter(staff => staff.category === category).map((staff, idx) => <div key={staff.name} className="flex flex-col space-y-1">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                              {staff.role}
                            </p>
                            <h5 className="font-serif font-bold text-foreground">{staff.name}</h5>
                            <Link to={`mailto:${staff.email}`} className="text-xs text-primary hover:underline font-mono flex items-center gap-1">
                              {staff.email}
                            </Link>
                          </div>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}